// T039: Card Loader (Legacy - wraps the new CardLoader)
// This file provides backwards compatibility with the old API

import type { CardDefinition, Faction } from '../../types';
import {
  loadAllCards,
  loadFactionCards,
  getCardDefinition,
  getAllFactions,
  getPlayableFactions as getPlayableFactionsList,
  searchCardsByName as searchCards,
  getCardsByType as getCardsOfType,
  getTotalCardCount as getCount,
  getNeutralCards,
  clearCardCache,
} from '../../services/CardLoader';

/**
 * Get all card definitions.
 */
export function getAllCards(): CardDefinition[] {
  return Array.from(loadAllCards().values());
}

/**
 * Get a card definition by ID.
 */
export function getCardById(cardId: string): CardDefinition | undefined {
  return getCardDefinition(cardId);
}

/**
 * Get all cards for a specific faction.
 */
export function getCardsByFaction(faction: Faction): CardDefinition[] {
  return loadFactionCards(faction);
}

/**
 * Get cards available for deck building (faction + neutral cards).
 */
export function getAvailableCardsForDeck(faction: Faction): CardDefinition[] {
  const factionCards = loadFactionCards(faction);
  const neutralCards = getNeutralCards();
  return [...factionCards, ...neutralCards];
}

/**
 * Get all factions with their display names.
 */
export function getFactions(): { id: Faction; nameZh: string }[] {
  return getAllFactions().map(f => ({
    id: f.id as Faction,
    nameZh: f.nameZh,
  }));
}

/**
 * Get playable factions (excludes neutral).
 */
export function getPlayableFactions(): { id: Faction; nameZh: string }[] {
  return getPlayableFactionsList().map(f => ({
    id: f.id as Faction,
    nameZh: f.nameZh,
  }));
}

/**
 * Create a card definitions map for game use.
 */
export function createDefinitionsMap(): Map<string, CardDefinition> {
  return new Map(loadAllCards());
}

/**
 * Search cards by name (supports partial match).
 */
export function searchCardsByName(query: string): CardDefinition[] {
  return searchCards(query);
}

/**
 * Get cards by type.
 */
export function getCardsByType(type: CardDefinition['type']): CardDefinition[] {
  return getCardsOfType(type);
}

/**
 * Get the total count of all cards.
 */
export function getTotalCardCount(): number {
  return getCount();
}

/**
 * Reset the database (for testing purposes).
 */
export function resetDatabase(): void {
  clearCardCache();
}

// Re-export as object for backwards compatibility
export const CardLoader = {
  getAllCards,
  getCardById,
  getCardsByFaction,
  getAvailableCardsForDeck,
  getFactions,
  getPlayableFactions,
  createDefinitionsMap,
  searchCardsByName,
  getCardsByType,
  getTotalCardCount,
  resetDatabase,
};
